import React, {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from "react";
import { FieldProps, Form, Input } from "collections";
import { Link, Redirect, Route, useRouteMatch } from "polyrouter";
import { Field } from "collections/Form";
import { LoadingIndicator, PolyIcon, SubmitButton, ToolTip } from "elements";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { getColumns } from "collections/Table";
import { ImportGroupsTable } from "./ImportGroupsTable";
import {
  AvailableExternalUserGroupsQuery,
  useAvailableExternalUserGroupsQuery,
  UserGroupSourceType,
  UsersForExternalGroupsQuery,
  useUsersForExternalGroupsQuery
} from "generated/graphql";
import { FieldArray, FormikErrors } from "formik";
import * as yup from "yup";
import { InferType } from "yup";
import { InternalRoleNames } from "roles";
import "routes/Admin/Accounts/Members/InviteMemberModal.scss";
import { useMicrosoftAuth } from "routes/Admin/Accounts/Integrations/useMicrosoftAuth";
import { noLogoutOnAuthErrorContext } from "polygraphql";
import { GenericUserPendingCheck } from "./importGroupsTypes";
import { isValidInternalEmail } from "utilities/stringUtils";
import { SECOND_IN_MILLISECONDS } from "Constants";

enum InviteOptions {
  NOW = "NOW",
  LATER = "LATER"
}
// enumerates the possible pages in the group import workflow
class ImportPage {
  static readonly SELECT = new ImportPage(
    "SELECT",
    "IMPORT_USER_GROUPS_TITLE",
    "/select",
    "full overflow pb-0"
  );
  static readonly OPTIONS = new ImportPage(
    "OPTIONS",
    "IMPORT_USER_GROUPS_OPTIONS_TITLE",
    "/options",
    "fixed-xl"
  );
  static readonly INVITE = new ImportPage(
    "INVITE",
    "IMPORT_USER_GROUPS_TITLE",
    "/invite",
    "invite-member"
  );
  static readonly CONFIRM = new ImportPage(
    "CONFIRM",
    "IMPORT_USER_GROUPS_TITLE",
    "/confirm",
    ""
  );
  // private to disallow creating other instances of this type
  private constructor(
    private readonly key: string,
    public readonly titleKey: LocalKeys,
    public readonly nestedRoute: string,
    public readonly formSize: string
  ) {}

  toString() {
    return this.key;
  }
}

const { SELECT, OPTIONS, INVITE, CONFIRM } = ImportPage;

const ImportGroupsFormSchema = yup.object({
  email: yup
    .string()
    .default("")
    .email(i18n.t("INVALID_EMAIL"))
    .label(i18n.t("EMAIL_ADDRESS"))
    .test("Email is in list", i18n.t("IS_IN_LIST"), function(email) {
      const { emails } = this.parent;
      if (emails.length) {
        const regex = new RegExp(emails.join("|"), "i"); // this adds case insensitivity to the list of emails
        return !regex.test(email);
      }
      return true;
    })
    .test(
      "Member is already on account",
      i18n.t("MEMBER_ALREADY_ON_ACCOUNT"),
      function(email) {
        const tenantMemberEmails = this.parent.tenantMemberEmails as string[];
        if (tenantMemberEmails.length) {
          const regex = new RegExp(tenantMemberEmails.join("|"), "i"); // this adds case insensitivity to the list of emails
          return !regex.test(email);
        }
        return true;
      }
    )
    .test(
      "internal-email-required",
      i18n.t("INTERNAL_EMAIL_REQUIRED"),
      function(email) {
        return (
          !InternalRoleNames.includes(this.parent.role) ||
          isValidInternalEmail(email)
        );
      }
    ),
  emails: yup
    .array()
    .default([])
    .of(yup.string()),
  groups: yup
    .array()
    .default([])
    .of(yup.string()),
  tenantMemberEmails: yup
    .array()
    .of(yup.string())
    .default([])
});

type ImportGroupsFormProps = InferType<typeof ImportGroupsFormSchema>;

export const ImportGroupsForm: React.FC<{
  tenantId: string;
  isValid: boolean;
  dirty: boolean;
  values: ImportGroupsFormProps;
  isSubmitting: boolean;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  setErrors: (
    errors: FormikErrors<{
      email: string;
      emails: string[];
      groups: string[];
      tenantMemberEmails: string[];
    }>
  ) => void;
  validateField: (field: string) => void;
  selectedGroups: Array<string>;
  setSelectedGroups: React.Dispatch<React.SetStateAction<string[]>>;
  pendingEmails: Array<string>;
  setPendingEmails: React.Dispatch<React.SetStateAction<string[]>>;
  selectedOption: InviteOptions | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<InviteOptions | null>>;
  adGroupData: AvailableExternalUserGroupsQuery | undefined;
  setAdGroupData: React.Dispatch<
    React.SetStateAction<AvailableExternalUserGroupsQuery | undefined>
  >;
}> = ({
  tenantId,
  isValid,
  values,
  isSubmitting,
  setFieldValue,
  setErrors,
  validateField,
  selectedGroups,
  setSelectedGroups,
  pendingEmails,
  setPendingEmails,
  selectedOption,
  setSelectedOption,
  adGroupData,
  setAdGroupData
}) => {
  const {
    data: adGroupDataResponse,
    error: adGroupError,
    executor: adGroupDataExecutor,
    fetching: adGroupFetching
  } = useAvailableExternalUserGroupsQuery({
    variables: {
      tenantId: tenantId,
      source: UserGroupSourceType.ActiveDirectory
    },
    requestPolicy: "network-only",
    context: noLogoutOnAuthErrorContext
  });

  useEffect(() => {
    setAdGroupData(adGroupDataResponse);
  }, [adGroupDataResponse, setAdGroupData]);

  const { getAccessToken } = useMicrosoftAuth();
  const [retryQueries, setRetryQueries] = useState(false);

  useEffect(() => {
    if (!adGroupFetching && retryQueries && adGroupError) {
      const retryExecutors = async () => {
        await new Promise(resolve =>
          setTimeout(resolve, 5 * SECOND_IN_MILLISECONDS)
        );
        adGroupDataExecutor({
          requestPolicy: "network-only"
        });
      };
      retryExecutors();
    }
  }, [retryQueries, adGroupError, adGroupFetching, adGroupDataExecutor]);

  const grantPermissions = async () => {
    const token = await getAccessToken();
    if (token) {
      setRetryQueries(true);
    }
  };

  const { t } = useTranslation();
  const root = useRouteMatch().path;
  const { url } = useRouteMatch();

  const cancelURL = url.replace("/group/import", "");
  const urlEndPath = "/group/import";

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.EXTERNAL_USER_GROUPS;
  const { [TABLE_COLUMN_STATE.SHOWN]: shownColumns } = useSelector(
    (state: State) => {
      return state.userMeta?.tables[TABLE_NAME] ?? {};
    }
  );

  const columnTranslations = {
    [COLUMNS.NAME]: t("NAME"),
    [COLUMNS.DESCRIPTION]: t("DESCRIPTION")
  };
  const shown = getColumns(shownColumns, columnTranslations);

  let backNavFromConfirm = INVITE.nestedRoute;
  if (!(selectedOption === InviteOptions.NOW)) {
    backNavFromConfirm = OPTIONS.nestedRoute;
    if (pendingEmails.length === 0) {
      backNavFromConfirm = OPTIONS.nestedRoute;
    }
  }

  return (
    <div className={""}>
      {adGroupError && (
        <MicrosoftToken
          grantPermissions={grantPermissions}
          cancelURL={cancelURL}
          retrying={
            retryQueries && ((adGroupFetching || adGroupError) as boolean)
          }
        />
      )}
      {!adGroupError && (
        <Form data-testid={`${"title"}-form`}>
          <Route exact path={root}>
            <Redirect to={`${root}${SELECT.nestedRoute}`} />
          </Route>
          <Route nest path={SELECT.nestedRoute}>
            {!adGroupFetching && (
              <>
                <div>{t("IMPORT_USER_GROUPS_DESCRIPTION")}</div>
                <p>{t("IMPORT_USER_GROUPS_INSTRUCTIONS")}</p>
                <span>
                  <ImportGroupsTable
                    userGroups={adGroupData?.userGroupsForTenant ?? []}
                    userCanEdit={true}
                    selectedItems={values.groups}
                    setSelectedItems={(items: string[]) => {
                      setFieldValue("groups", items);
                      setSelectedGroups(items);
                    }}
                    loading={adGroupFetching}
                    fetching={adGroupFetching}
                    shown={shown}
                  />
                </span>
              </>
            )}
            {adGroupFetching && <LoadingIndicator />}
            <FooterButtons
              className="footer-bottom-right"
              canProgress={values.groups.length > 0}
              nextPage={OPTIONS}
              cancelURL={cancelURL}
              urlEndPath={urlEndPath}
            />
          </Route>
          <Route nest path={OPTIONS.nestedRoute}>
            <div className="form-content">
              <InviteSelector
                selectedOption={selectedOption}
                onSelectedOptionChange={setSelectedOption}
                tenantId={tenantId}
                selectedGroups={selectedGroups}
                pendingEmails={pendingEmails}
                setPendingEmails={setPendingEmails}
                cancelURL={cancelURL}
                urlEndPath={urlEndPath}
              />
              <FooterButtons
                canProgress={selectedOption != null}
                prevPage={SELECT}
                nextPage={
                  selectedOption === InviteOptions.NOW ? INVITE : CONFIRM
                }
                cancelURL={cancelURL}
                urlEndPath={urlEndPath}
              />
            </div>
          </Route>
          <Route nest path={INVITE.nestedRoute}>
            <EmailPicker
              isSubmitting={isSubmitting}
              isValid={isValid}
              setFieldValue={setFieldValue}
              validateField={validateField}
              setErrors={setErrors}
              values={values}
            />
            <FooterButtons
              canProgress={isValid}
              prevPage={OPTIONS}
              nextPage={CONFIRM}
              cancelURL={cancelURL}
              urlEndPath={urlEndPath}
            />
          </Route>
          <Route nest path={CONFIRM.nestedRoute}>
            <div className="form-content">
              <div>
                {t("IMPORT_USER_GROUPS_CONFIRM", {
                  count: values.groups.length
                })}
              </div>
              {selectedOption === InviteOptions.NOW && (
                <p>
                  {t("IMPORT_USER_GROUPS_CONFIRM_WITH_INVITE", {
                    count: values.emails.length + (values.email ? 1 : 0)
                  })}
                </p>
              )}
            </div>
            <div className="btn-container">
              <Link
                to={`${cancelURL}${urlEndPath}${backNavFromConfirm}`}
                className="btn btn-secondary"
              >
                {t("BACK")}
              </Link>
              <Link to={cancelURL} className="btn">
                {t("CANCEL")}
              </Link>
              <Field name="submit">
                {({ form: { isSubmitting } }: FieldProps) => (
                  <SubmitButton
                    isSubmitting={isSubmitting}
                    testId="save"
                    labelKey="CONFIRM"
                    submittingLabelKey="SAVING"
                  />
                )}
              </Field>
            </div>
          </Route>
        </Form>
      )}
    </div>
  );
};

const MicrosoftToken: React.FC<{
  grantPermissions: () => Promise<void>;
  cancelURL: string;
  retrying: boolean;
}> = ({ grantPermissions, cancelURL, retrying }) => {
  const { t } = useTranslation();
  return (
    <>
      <p>{t("IMPORT_USER_GROUPS_MICROSOFT_PERMISSIONS_PROMPT")}</p>
      <div className="btn-container">
        <Link to={cancelURL} className="btn" disabled={retrying}>
          {t("CANCEL")}
        </Link>
        <button
          onClick={grantPermissions}
          type="button"
          className="btn btn-primary"
          disabled={retrying}
        >
          <span>{t("GRANT_PERMISSIONS")}</span>
        </button>
      </div>
      {retrying && <LoadingIndicator className="pt-2" />}
    </>
  );
};

const InviteSelector: React.FC<{
  selectedOption: InviteOptions | null;
  onSelectedOptionChange: Dispatch<SetStateAction<InviteOptions | null>>;
  tenantId: string;
  selectedGroups: Array<string>;
  pendingEmails: Array<string>;
  setPendingEmails: React.Dispatch<React.SetStateAction<string[]>>;
  cancelURL: string;
  urlEndPath: string;
}> = ({
  tenantId,
  selectedOption,
  onSelectedOptionChange,
  selectedGroups,
  pendingEmails,
  setPendingEmails,
  cancelURL,
  urlEndPath
}) => {
  const { t } = useTranslation();

  const {
    data: extUsersData,
    fetching: extUsersFetching,
    stale: extUsersStale
  } = useUsersForExternalGroupsQuery({
    variables: {
      tenantId: tenantId,
      source: UserGroupSourceType.ActiveDirectory,
      groupIds: selectedGroups.filter(item => item !== "HEADER_ID")
    }
  });

  const getPendingEmails = useCallback(
    (extUsersQuery: UsersForExternalGroupsQuery | undefined): Array<string> => {
      const emailSet =
        extUsersQuery?.userGroupsForTenant?.reduce(
          (acc: Set<string>, value) => {
            if (value?.genericUsers?.__typename === "GenericUserList") {
              value.genericUsers?.items?.forEach(
                (genericUser: GenericUserPendingCheck) => {
                  if (!genericUser?.user?.user && genericUser.email) {
                    acc.add(genericUser.email ?? "");
                  }
                }
              );
            }
            return acc;
          },
          new Set<string>()
        ) ?? new Set<string>();
      return Array.from(emailSet);
    },
    []
  );

  useEffect(() => {
    const parsedPendingEmails = getPendingEmails(extUsersData);
    setPendingEmails(parsedPendingEmails);
    if (!extUsersFetching && parsedPendingEmails.length === 0) {
      onSelectedOptionChange(InviteOptions.LATER);
    }
  }, [
    extUsersFetching,
    extUsersData,
    setPendingEmails,
    onSelectedOptionChange,
    getPendingEmails
  ]);

  return (
    <>
      {extUsersFetching && <LoadingIndicator />}
      {!extUsersFetching && (
        <>
          <div>
            {t("IMPORT_USER_GROUPS_OPTIONS_DESCRIPTION", {
              count: pendingEmails.length
            })}
          </div>
          <div className="form-input">
            <div>
              <input
                type="radio"
                id={InviteOptions.NOW}
                aria-label={t("IMPORT_USER_GROUPS_OPTION_NOW")}
                name="inviteOption"
                value={InviteOptions.NOW}
                defaultChecked={selectedOption === InviteOptions.NOW}
                onClick={() => onSelectedOptionChange(InviteOptions.NOW)}
              />
              <label className="inline option inventory-view-option">
                {t("IMPORT_USER_GROUPS_OPTION_NOW")}
              </label>
            </div>
            <div>
              <input
                type="radio"
                id={InviteOptions.LATER}
                name="inviteOption"
                aria-label={t("IMPORT_USER_GROUPS_OPTION_LATER")}
                value={InviteOptions.LATER}
                defaultChecked={selectedOption === InviteOptions.LATER}
                onClick={() => onSelectedOptionChange(InviteOptions.LATER)}
              />
              <label className="inline option inventory-view-option">
                {t("IMPORT_USER_GROUPS_OPTION_LATER")}
              </label>
            </div>
            {selectedOption === InviteOptions.LATER &&
              !extUsersFetching &&
              !extUsersStale &&
              getPendingEmails(extUsersData).length === 0 && (
                <Redirect
                  to={`${cancelURL}${urlEndPath}${CONFIRM.nestedRoute}`}
                />
              )}
          </div>
        </>
      )}
    </>
  );
};

const FooterButtons: React.FC<{
  prevPage?: ImportPage;
  nextPage: ImportPage;
  canProgress: boolean;
  cancelURL: string;
  urlEndPath: string;
  className?: string;
}> = ({
  prevPage,
  nextPage,
  canProgress,
  cancelURL,
  urlEndPath,
  className
}) => {
  const { t } = useTranslation();
  const containerClassname = className
    ? `btn-container ${className}`
    : "btn-container";
  return (
    <div className={containerClassname}>
      {prevPage && (
        <Link
          to={`${cancelURL}${urlEndPath}${prevPage.nestedRoute}`}
          className="btn btn-secondary"
        >
          {t("BACK")}
        </Link>
      )}
      <Link to={cancelURL} className="btn">
        {t("CANCEL")}
      </Link>
      <Link
        to={`${cancelURL}${urlEndPath}${nextPage.nestedRoute}`}
        className="btn btn-primary"
        disabled={!canProgress}
      >
        {t("CONTINUE")}
      </Link>
    </div>
  );
};

const EmailPicker: React.FC<{
  isSubmitting: boolean;
  isValid: boolean;
  setFieldValue: any;
  validateField: (field: string) => void;
  setErrors: any;
  values: ImportGroupsFormProps;
}> = ({
  isSubmitting,
  isValid,
  setFieldValue,
  validateField,
  setErrors,
  values
}) => {
  const { t } = useTranslation();

  const handleEmailChange = (
    event: SyntheticEvent,
    isValid: boolean,
    setFieldValue: (
      field: keyof ImportGroupsFormProps,
      value: any,
      shouldValidate?: boolean
    ) => void,
    pushValue: (obj: any) => void,
    validateField: (field: string) => void
  ) => {
    validateField("email");
    if (
      ["Enter", "Tab", ",", ";", " "].includes((event as KeyboardEvent).key)
    ) {
      event.preventDefault();
      const inputValue = (event.currentTarget as HTMLTextAreaElement).value;
      if (inputValue === "") {
        return;
      }
      if (isValid) {
        setFieldValue("email", "", false);
        pushValue(inputValue);
      }
    }
  };

  const handlePaste = (
    event: SyntheticEvent,
    setFieldValue: (
      field: keyof ImportGroupsFormProps,
      value: any,
      shouldValidate?: boolean
    ) => void,
    setErrors: (errors: FormikErrors<object & { email: string }>) => void,
    values: ImportGroupsFormProps
  ) => {
    event.preventDefault();
    const data = (event as ClipboardEventInit).clipboardData;
    if (data) {
      const paste = data.getData("text");
      setFieldValue("email", paste, false);
      const newEmails = paste.match(/[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/g) ?? [];
      const emails = values.emails ?? [];

      if (newEmails.length > 0) {
        // ensure unique emails
        const emailFieldValue = Array.from(
          new Set<string>([...emails, ...newEmails])
        );
        setFieldValue("emails", emailFieldValue);
        setFieldValue("email", "");
        return;
      }
    }
    setErrors({ email: `${t("TRY_AGAIN")}` });
    return;
  };

  const addressTooltip = () => {
    return (
      <ToolTip
        size="lg"
        position="top"
        icon="help_circle"
        iconTitle="help"
        className="ml-14 mb-0 help"
      >
        {t("ENTER_ADDRESS_TOOLTIP_INFO")}
      </ToolTip>
    );
  };

  return (
    <div>
      {isSubmitting ? (
        <p>{t("SENDING")}</p>
      ) : (
        <>
          <div className="mb-5">
            {`${t("ENTER_ADDRESS_INSTRUCTIONS")}.`}
            {addressTooltip()}
            {t("ENTER_ADDRESS_INSTRUCTIONS_PASTE")}
          </div>
          <FieldArray
            name="emails"
            render={arrayHelpers => (
              <>
                <div className="form-content">
                  <Input
                    data-testid="email-input"
                    className="material on-black form-control"
                    id="email"
                    name="email"
                    label={t("EMAIL_ADDRESS")}
                    onKeyDown={event =>
                      handleEmailChange(
                        event,
                        isValid,
                        setFieldValue,
                        arrayHelpers.push,
                        validateField
                      )
                    }
                    onPaste={event =>
                      handlePaste(event, setFieldValue, setErrors, values)
                    }
                  />
                </div>
                <div className="scroll-content">
                  {values.emails.map((email, i) => (
                    <span
                      className="email-chip mt-5"
                      data-testid="email-chip"
                      key={email}
                    >
                      <span className="grid">
                        <span className="col-10 p-5 v-center">{email}</span>
                        <span
                          onClick={() => arrayHelpers.remove(i)}
                          className="col-2 text-right v-center"
                        >
                          <PolyIcon icon="clear_x" className="clear-chip" />
                        </span>
                      </span>
                    </span>
                  ))}
                </div>
              </>
            )}
          />
        </>
      )}
    </div>
  );
};
